import { Box, Chip } from "@mui/material";
import { GridOverlay, GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import DetailsHoverPopOverList from "../../../../../../../components/DetailsHoverPopOverList";
import Flex from "../../../../../../../components/Flex";
import { GlobalTableHeightWrapper } from "../../../../../../../components/GlobalTableHeightWrapper";
import { MultiSelect } from "../../../../../../../components/MultiSelect";
import RenderWithTooltip from "../../../../../../../components/RenderWithTooltip";
import Text from "../../../../../../../components/Text";
import ActionMenu from "../../../../../../../components/app/ActionMenu";
import { useRoleController } from "../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useGetPatientPaymentSummary } from "../../../../../../../queries/billings/payments/use-get-patient-payment-summary";
import { useGetPatientPaymentV2 } from "../../../../../../../queries/billings/payments/use-get-patient-payment-v2";
import { NonNullableDate } from "../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  formatMoney,
  replaceUnderScoreAddSpace,
  roleFilter,
} from "../../../../../../../utils/utils";
import ViewPatientPayment from "../../../../billings/payments/ViewPatientPayment";
import { getPaymentModeLabel } from "../../../../billings/payments/patient-payments-constant";
import UpsertMedicalHistoryDrawer from "../history/UpsertMedicalHistoryDrawer";

// const PAYMENT_METHOD_LABEL: Record<string, string> = {
//   [PatientCardPaymentModeEnum.Card]: "Card",
//   [PatientCardPaymentModeEnum.Online]: "Card",
//   [PatientCardPaymentModeEnum.Cash]: "Cash",
//   [PatientCardPaymentModeEnum.Bank]: "Bank (ACH)",
//   [PatientCardPaymentModeEnum.PatientWallet]: "Patient Wallet",
// };

// Category labels now come from the shared PATIENT_PAYMENT_CATEGORY_OPTIONS.
// const PAYMENT_CATEGORY_LABEL: Record<string, string> = {
//   [PatientCardPaymentCategoryEnum.Copay]: "Copay",
//   [PatientCardPaymentCategoryEnum.Deductible]: "Deductible",
//   [PatientCardPaymentCategoryEnum.Coinsurance]: "Coinsurance",
//   [PatientCardPaymentCategoryEnum.SelfPay]: "Self-Pay",
//   [PatientCardPaymentCategoryEnum.Advance]: "Advance",
// };

const PAYMENT_STATUS = {
  Paid: "PAID",
  PartiallyPaid: "PARTIALLY_PAID",
  Pending: "PENDING",
} as const;

const STATUS_LABEL: Record<string, string> = {
  [PAYMENT_STATUS.Paid]: "Paid",
  [PAYMENT_STATUS.PartiallyPaid]: "Partially Paid",
  [PAYMENT_STATUS.Pending]: "Pending",
};

const STATUS_COLOR: Record<string, { color: string; bgColor: string }> = {
  [PAYMENT_STATUS.Paid]: { color: "#00B917", bgColor: "#E5F8E7" },
  [PAYMENT_STATUS.PartiallyPaid]: { color: "#FFAA00", bgColor: "#FFF4E4" },
  [PAYMENT_STATUS.Pending]: { color: "#FF0000", bgColor: "#FDECEC" },
};

// ECRH-5397: Status listing shows only Paid / Partially Paid.
const STATUS_FILTER_OPTIONS = [
  { value: PAYMENT_STATUS.Paid, label: "Paid" },
  { value: PAYMENT_STATUS.PartiallyPaid, label: "Partially Paid" },
  // { value: PAYMENT_STATUS.Pending, label: "Pending" },
];

// Empty monetary values now render as $ 0.00 (shared formatMoney) instead of
// a dash. Local helper kept commented for reference:
// const formatMoney = (v: unknown) => {
//   if (v === null || v === undefined || v === "") return "-";
//   const n = Number(v);
//   if (!Number.isFinite(n)) return "-";
//   return `$ ${n.toFixed(2)}`;
// };

// ECRH-5397: Remaining Balance / Unused Payment columns removed from the
// standardized listing, so this helper is no longer used (kept for reference).
// const computedMoney = (n: number) =>
//   `$ ${(Number.isFinite(n) ? n : 0).toFixed(2)}`;

// Amount colours for the balance chips only: the Credit Balance value renders
// green and the Outstanding Balance value renders red. The table amount columns
// keep the system default text colour.
const AMOUNT_GREEN = "#4CC76A";
// Used by the Outstanding Balance tag, now rendered on the Invoices tab.
// const AMOUNT_RED = "#F26161";

const toNumber = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// ECRH-5397: only referenced by the commented-out Collected By column now.
// const dash = (v: unknown) => {
//   if (v === null || v === undefined || v === "") return "-";
//   return v;
// };

const PatientPaymentsNew = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  // Active balance tag filter (AC2/AC3). Only one tag can be active at a time;
  // clicking the active tag again clears it back to the default view (AC4).
  const [balanceFilter, setBalanceFilter] = useState<
    "credit" | "outstanding" | null
  >(null);
  const { hasReadOnly } = useRoleController("Financials");
  const patientId = useParams()?.patientId ?? "";

  // Patient-level balance tags (AC1): creditBalance / previousBalance from by-visit.
  const { data: paymentSummary } = useGetPatientPaymentSummary({
    patientUuid: patientId,
  });

  const medicationDisclosure = useDisclosure(false);
  const viewPatientPaymentDisclosureHost = useDisclosure(false);

  const {
    data: patientPayment,
    isFetching,
    isLoading,
  } = useGetPatientPaymentV2({
    pagination: pagination,
    typeOfPayment: "PATIENT",
    gridSorting,
    status: statusFilter.length ? statusFilter.join(",") : undefined,
    creditBalance: balanceFilter === "credit" ? true : undefined,
    outstandingBalance: balanceFilter === "outstanding" ? true : undefined,
    patientUuid: patientId,
  });

  // Toggle a balance tag filter: clicking the active tag clears it (AC4),
  // clicking the other tag switches to it (only one active at a time).
  const toggleBalanceFilter = (filter: "credit" | "outstanding") => {
    setBalanceFilter((prev) => (prev === filter ? null : filter));
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const { hasView: hasViewPayments } = useRoleController("Apply Payments");

  const COLUMNS = applyDefaultColumnSettings<any>(
    [
      // Standardized column sequence (ECRH-5397): Transaction ID, Payment Mode,
      // Paid Amount, Applied Amount, Unapplied Amount, Visit ID, Invoice ID,
      // Category, Transaction Date, Status. Patient Name is intentionally omitted
      // here (shown only in Billing → Payments → Patient Payment).
      {
        field: "transactionId",
        flex: 0.45,
        minWidth: 120,
        sortable: false,
        disableColumnMenu: true,
        headerName: "Transaction ID",
        renderCell: ({ row }) => (
          <RenderWithTooltip
            value={row?.transactionId || row?.chequeId || "-"}
          />
        ),
      },
      {
        field: "paymentMethod",
        flex: 0.45,
        minWidth: 120,
        sortable: false,
        disableColumnMenu: true,
        headerName: "Payment Mode",
        renderCell: ({ row }) => (
          <Text isTabletext>{getPaymentModeLabel(row?.paymentMode)}</Text>
        ),
      },
      {
        field: "amount",
        flex: 0.35,
        minWidth: 110,
        sortable: false,
        disableColumnMenu: true,
        headerName: "Paid Amount",
        renderCell: ({ row }) => formatMoney(row?.amount),
      },
      {
        field: "appliedAmount",
        flex: 0.35,
        minWidth: 120,
        sortable: false,
        disableColumnMenu: true,
        headerName: "Applied Amount",
        renderCell: ({ row }) => formatMoney(row?.appliedAmount),
      },
      {
        field: "unappliedAmount",
        flex: 0.4,
        minWidth: 135,
        sortable: false,
        disableColumnMenu: true,
        headerName: "Unapplied Amount",
        renderCell: ({ row }) => formatMoney(row?.unappliedAmount),
      },
      {
        field: "visitIds",
        flex: 0.4,
        minWidth: 110,
        sortable: false,
        disableColumnMenu: true,
        headerName: "Visit ID",
        renderCell: ({ row }) => {
          const visitIdsObj = row?.visitIds || {};
          const visitIdKeys = Object.keys(visitIdsObj);
          if (!visitIdKeys.length) return <Text isTabletext>-</Text>;
          return <DetailsHoverPopOverList data={visitIdKeys} />;
        },
      },
      {
        field: "billIds",
        flex: 0.4,
        minWidth: 110,
        sortable: false,
        disableColumnMenu: true,
        headerName: "Invoice ID",
        renderCell: ({ row }) => {
          const billIdsObj = row?.billIds || {};
          const billIdKeys = Object.keys(billIdsObj);
          if (!billIdKeys.length) return <Text isTabletext>-</Text>;
          return <DetailsHoverPopOverList data={billIdKeys} />;
        },
      },
      {
        field: "paymentCategory",
        flex: 0.45,
        minWidth: 120,
        sortable: false,
        disableColumnMenu: true,
        headerName: "Category",
        renderCell: ({ row }) => (
          <Text isTabletext>
            {replaceUnderScoreAddSpace(row?.paymentCategory?.toLowerCase()) ||
              "-"}
          </Text>
        ),
      },
      {
        field: "transactionDate",
        flex: 0.45,
        minWidth: 130,
        disableColumnMenu: true,
        headerName: "Transaction Date",
        renderCell: ({ row }) =>
          row?.paymentDate ? NonNullableDate(row?.paymentDate) : "-",
      },
      // ECRH-5397: removed from the standardized listing (kept for reference).
      // {
      //   field: "remainingBalance",
      //   flex: 0.4,
      //   minWidth: 140,
      //   sortable: false,
      //   disableColumnMenu: true,
      //   headerName: "Remaining Balance",
      //   // Billed − Paid (clamped to 0 when fully paid or overpaid).
      //   renderCell: ({ row }) =>
      //     computedMoney(
      //       Math.max(0, toNumber(row?.appliedAmount) - toNumber(row?.amount))
      //     ),
      // },
      // {
      //   field: "unusedPayment",
      //   flex: 0.4,
      //   minWidth: 135,
      //   sortable: false,
      //   disableColumnMenu: true,
      //   headerName: "Unused Payment",
      //   // Paid − Billed (only when payment exceeds billed, else 0).
      //   renderCell: ({ row }) =>
      //     computedMoney(
      //       Math.max(0, toNumber(row?.amount) - toNumber(row?.appliedAmount))
      //     ),
      // },
      // {
      //   field: "collectedBy",
      //   flex: 0.45,
      //   minWidth: 130,
      //   sortable: false,
      //   disableColumnMenu: true,
      //   headerName: "Collected By",
      //   renderCell: ({ row }) => (
      //     <RenderWithTooltip value={dash(row?.collectedBy)} />
      //   ),
      // },
      {
        field: "status",
        flex: 0.4,
        minWidth: 130,
        sortable: false,
        disableColumnMenu: true,
        headerName: "Status",
        renderCell: ({ row }) => {
          const s = row?.paymentStatus;
          if (!s) return <Text isTabletext>-</Text>;
          const cfg = STATUS_COLOR[s];
          return (
            <Chip
              size="small"
              label={
                STATUS_LABEL[s] ?? replaceUnderScoreAddSpace(s?.toLowerCase())
              }
              sx={{
                color: cfg?.color,
                bgcolor: cfg?.bgColor,
                padding: "4px 2px",
              }}
            />
          );
        },
      },
      {
        field: "Action",
        width: 90,
        hideSortIcons: true,
        sortable: false,
        disableColumnMenu: true,
        headerName: "Action",
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={roleFilter({
                list: [{ value: "view", label: "View Payment" }],
                compWith: {
                  view: hasViewPayments,
                },
              })}
              onActionClick={(v) => {
                if (v === "view") {
                  viewPatientPaymentDisclosureHost.open(row?.uuid);
                }
              }}
            />
          );
        },
      },
    ],
    ["transactionDate"]
  );

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <UpsertMedicalHistoryDrawer disclosureHost={medicationDisclosure} />
      <ViewPatientPayment
        title="View Payment"
        disclosureHost={viewPatientPaymentDisclosureHost}
      />

      <Flex sx={{ mb: 1, gap: 1, alignItems: "center" }}>
        <Chip
          label={
            <>
              Credit Balance:{" "}
              <Box
                component="span"
                sx={{ color: AMOUNT_GREEN, fontWeight: 600 }}
              >
                $ {toNumber(paymentSummary?.data?.creditBalance).toFixed(2)}
              </Box>
            </>
          }
          onClick={() => toggleBalanceFilter("credit")}
          variant="outlined"
          sx={{
            bgcolor: "transparent",
            cursor: "pointer",
            borderColor:
              balanceFilter === "credit" ? "text.primary" : "divider",
            "&:hover": { bgcolor: "transparent" },
          }}
        />
        {/* Outstanding Balance tag moved to the Invoices tab (PatientBills).
        Kept for reference:

        <Chip
          label={
            <>
              Outstanding Balance:{" "}
              <Box component="span" sx={{ color: AMOUNT_RED, fontWeight: 600 }}>
                $ {toNumber(paymentSummary?.data?.previousBalance).toFixed(2)}
              </Box>
            </>
          }
          onClick={() => toggleBalanceFilter("outstanding")}
          variant="outlined"
          sx={{
            bgcolor: "transparent",
            cursor: "pointer",
            borderColor:
              balanceFilter === "outstanding" ? "text.primary" : "divider",
            "&:hover": { bgcolor: "transparent" },
          }}
        /> */}
        <Box sx={{ flex: 1 }} />
        <Box sx={{ width: 240 }}>
          <MultiSelect
            placeholder="Status"
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            optionWithCheckbox
            onChange={(vals) => {
              setStatusFilter(vals);
              setPagination((p) => ({ ...p, page: 0 }));
            }}
          />
        </Box>
      </Flex>

      <GlobalTableHeightWrapper marginTop={0}>
        <CustomDataGridV2
          getRowId={(row) => row?.uuid}
          rows={patientPayment?.data?.content ?? []}
          columns={filterActionCol(COLUMNS, hasReadOnly)}
          rowCount={patientPayment?.data?.page?.totalElements ?? 0}
          disableRowSelectionOnClick
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={gridSorting}
          onSortModelChange={setGridSorting}
          loading={isLoading || isFetching}
          slots={{
            noRowsOverlay: () => (
              <GridOverlay>
                <Text isTabletext>No payments recorded for this patient</Text>
              </GridOverlay>
            ),
          }}
        />
      </GlobalTableHeightWrapper>
    </Box>
  );
};

export default PatientPaymentsNew;
